import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './utils/authUtils';
import { nacmeLogo } from './assets/images';
import { MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

// Component for collapsible requirement dropdowns
const RequirementDropdown = ({ label, items, companyIndex, fieldName, expandedItems, setExpandedItems }) => {
  const dropdownKey = `${companyIndex}-${fieldName}`;
  const isExpanded = expandedItems.has(dropdownKey);
  
  const toggleDropdown = (e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(dropdownKey)) {
      newExpanded.delete(dropdownKey);
    } else {
      newExpanded.add(dropdownKey);
    }
    setExpandedItems(newExpanded);
  };

  if (!items || items.length === 0) {
    return (
      <div>
        <p className="font-medium text-gray-700 text-sm">{label}:</p>
        <p className="text-gray-600 text-sm">Not specified</p>
      </div>
    );
  }

  const displayText = items.length === 1 ? items[0] : `${items.length} items`;
  const shouldShowDropdown = items.length > 1;

  return (
    <div>
      <p className="font-medium text-gray-700 text-sm">{label}:</p>
      {shouldShowDropdown ? (
        <div>
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
          >
            <span>{displayText}</span>
            <svg 
              className={`w-3 h-3 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isExpanded && (
            <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
              <ul className="list-disc list-inside space-y-1">
                {items.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-600 text-sm">{displayText}</p>
      )}
    </div>
  );
};

const AllCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    fetchAllCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
  }, [companies, searchTerm, locationFilter]);

  const fetchAllCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/all-companies`);
      const sortedCompanies = (response.data.companies || []).sort((a, b) => 
        a.organization.toLowerCase().localeCompare(b.organization.toLowerCase())
      );
      setCompanies(sortedCompanies);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to load companies');
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = companies;

    if (searchTerm) {
      filtered = filtered.filter(company => {
        const searchLower = searchTerm.toLowerCase();
        return (
          company.organization.toLowerCase().includes(searchLower) ||
          (company.internship_description && company.internship_description.toLowerCase().includes(searchLower)) ||
          (company.eligible_majors && Array.isArray(company.eligible_majors) && 
           company.eligible_majors.some(major => major.toLowerCase().includes(searchLower))) ||
          (company.preferred_institutions && Array.isArray(company.preferred_institutions) && 
           company.preferred_institutions.some(inst => inst.toLowerCase().includes(searchLower))) ||
          (company.recruiter_name && company.recruiter_name.toLowerCase().includes(searchLower))
        );
      });
    }

    if (locationFilter) {
      filtered = filtered.filter(company => 
        company.company_locations && company.company_locations.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    setFilteredCompanies(filtered);
  };

  const toggleDescriptionExpansion = (recruiterId, e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedDescriptions);
    if (newExpanded.has(recruiterId)) {
      newExpanded.delete(recruiterId);
    } else {
      newExpanded.add(recruiterId);
    }
    setExpandedDescriptions(newExpanded);
  };

  const handleCompanyClick = (recruiterId) => {
    navigate('/register');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="companies"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            // Students button handled by grid menu
          } else if (tabId === 'recruiters') {
            // Recruiters button handled by grid menu
          } else if (tabId === 'university-partners') {
            // University Partners button handled by grid menu
          } else if (tabId === 'companies') {
            navigate('/companies');
          } else if (tabId === 'profile') {
            // Logout functionality
            localStorage.removeItem('token');
            navigate('/admin/login');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src={nacmeLogo}
            alt="NACME Logo"
            className="h-16 w-auto mx-auto mb-4 drop-shadow"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            All Partner Companies
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Explore all companies partnering with NACME. Click on any company to start your application.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Companies
              </label>
              <input
                type="text"
                placeholder="Search by company name, description, or major..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Location
              </label>
              <input
                type="text"
                placeholder="Enter state or city..."
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {filteredCompanies.length} of {companies.length} companies
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>

        {/* Company Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((company, index) => (
            <div
              key={company.recruiter_id}
              className="bg-white rounded-lg shadow-lg p-6 border-2 border-gray-200 hover:border-blue-300 transition-all cursor-pointer"
              onClick={() => handleCompanyClick(company.recruiter_id)}
            >
              {/* Company Header */}
              <div className="mb-4">
                {/* Company Logo */}
                {company.logo && (
                  <div className="mb-3 flex justify-center">
                    <img
                      src={company.logo.startsWith('/public/') ? company.logo.replace('/public/', '/') : company.logo}
                      alt={`${company.organization} logo`}
                      className={`w-auto object-contain ${
                        ['Phillips 66', 'Phillips66'].includes(company.organization) 
                          ? 'h-24' 
                          : company.organization === 'ITT' 
                          ? 'h-28' 
                          : company.organization === 'UPS' 
                          ? 'h-18' 
                          : ['Procter & Gamble', 'P&G', 'Bechtel', 'Boston Scientific'].includes(company.organization) 
                          ? 'h-20' 
                          : company.organization === 'Air Products' 
                          ? 'h-11' 
                          : company.organization === 'Autodesk' 
                          ? 'h-10' 
                          : company.organization === 'Langan' 
                          ? 'h-6' 
                          : company.organization === 'Qualcomm' 
                          ? 'h-8' 
                          : 'h-12'
                      }`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">
                  {company.organization}
                </h3>
                {company.approved && (
                  <div className="flex justify-center">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      APPROVED
                    </span>
                  </div>
                )}
              </div>

              {/* Basic Company Info */}
              <div className="space-y-3 mb-4">
                <div>
                  <p className="font-medium text-gray-700 text-sm">Minimum GPA:</p>
                  <p className="text-gray-600 text-sm">{company.minimum_gpa || 'Not specified'}</p>
                </div>
                
                {/* Internship Description */}
                {company.internship_description && (
                  <div>
                    <button
                      onClick={(e) => toggleDescriptionExpansion(company.recruiter_id, e)}
                      className="flex items-center space-x-1 font-bold text-blue-600 text-sm hover:text-blue-700 transition-colors"
                    >
                      <span>Internship Description</span>
                      <svg 
                        className={`w-3 h-3 transform transition-transform ${
                          expandedDescriptions.has(company.recruiter_id) ? 'rotate-180' : ''
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedDescriptions.has(company.recruiter_id) && (
                      <div className="mt-2 p-3 bg-gray-50 rounded border text-sm text-gray-700">
                        {company.internship_description.split('\n').map((paragraph, idx) => (
                          <p key={idx} className={idx > 0 ? 'mt-3' : ''}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div>
                  <p className="font-medium text-gray-700 text-sm">Locations:</p>
                  <p className="text-gray-600 text-sm">{company.company_locations || 'Not specified'}</p>
                </div>
                
                {/* External Application Link */}
                {company.external_application_link && company.external_application_link !== 'null' && (
                  <div>
                    <p className="font-medium text-gray-700 text-sm">Application Link:</p>
                    <a
                      href={company.external_application_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm hover:text-blue-700 underline break-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Apply Online
                    </a>
                  </div>
                )}
              </div>

              {/* Requirements */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <RequirementDropdown 
                  label="Eligible Majors"
                  items={company.eligible_majors}
                  companyIndex={index}
                  fieldName="majors"
                  expandedItems={expandedItems}
                  setExpandedItems={setExpandedItems}
                />
                <RequirementDropdown 
                  label="Preferred Institutions"
                  items={company.preferred_institutions}
                  companyIndex={index}
                  fieldName="institutions"
                  expandedItems={expandedItems}
                  setExpandedItems={setExpandedItems}
                />
                <div>
                  <p className="font-medium text-gray-700 text-sm">Preferred Class Years:</p>
                  <p className="text-gray-600 text-sm">
                    {company.preferred_class_years ? company.preferred_class_years.join(', ') : 'Not specified'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCompanies.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <p className="text-gray-600 text-lg font-medium">
              No companies found matching your criteria
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Try adjusting your search or filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllCompanies;